import { isBrowser, localStorageUtils } from '@paneon/core';
import { createContext, useState, useMemo, useEffect, useCallback, useContext } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/colorThemes.ts
var paneonNaturalTheme = {
  id: "paneon",
  name: "Paneon Natural",
  colors: {
    primary: "#4CAF50",
    primaryLight: "#81C784",
    primaryDark: "#388E3C",
    secondary: "#FF9800",
    secondaryLight: "#FFB74D",
    secondaryDark: "#F57C00",
    background: "#FAFAFA"
  },
  gradient: "linear-gradient(135deg, #4CAF50 0%, #81C784 50%, #A5D6A7 100%)",
  glowColor: "rgba(76, 175, 80, 0.6)",
  textColor: "rgba(0, 0, 0, 0.87)",
  textColorSecondary: "rgba(0, 0, 0, 0.6)"
};
var oceanBlueTheme = {
  id: "ocean",
  name: "Ocean Blue",
  colors: {
    primary: "#2196F3",
    primaryLight: "#64B5F6",
    primaryDark: "#1976D2",
    secondary: "#00BCD4",
    secondaryLight: "#4DD0E1",
    secondaryDark: "#0097A7",
    background: "#F5F9FC"
  },
  gradient: "linear-gradient(135deg, #2196F3 0%, #64B5F6 50%, #90CAF9 100%)",
  glowColor: "rgba(33, 150, 243, 0.6)",
  textColor: "rgba(0, 0, 0, 0.87)",
  textColorSecondary: "rgba(0, 0, 0, 0.6)"
};
var sunsetOrangeTheme = {
  id: "sunset",
  name: "Sunset Orange",
  colors: {
    primary: "#FF5722",
    primaryLight: "#FF8A65",
    primaryDark: "#E64A19",
    secondary: "#FFC107",
    secondaryLight: "#FFD54F",
    secondaryDark: "#FFA000",
    background: "#FFF8F5"
  },
  gradient: "linear-gradient(135deg, #FF5722 0%, #FF8A65 50%, #FFAB91 100%)",
  glowColor: "rgba(255, 87, 34, 0.6)",
  textColor: "rgba(0, 0, 0, 0.87)",
  textColorSecondary: "rgba(0, 0, 0, 0.6)"
};
var lavenderPurpleTheme = {
  id: "lavender",
  name: "Lavender Purple",
  colors: {
    primary: "#9C27B0",
    primaryLight: "#BA68C8",
    primaryDark: "#7B1FA2",
    secondary: "#E91E63",
    secondaryLight: "#F06292",
    secondaryDark: "#C2185B",
    background: "#FAF5FC"
  },
  gradient: "linear-gradient(135deg, #9C27B0 0%, #BA68C8 50%, #CE93D8 100%)",
  glowColor: "rgba(156, 39, 176, 0.6)",
  textColor: "rgba(0, 0, 0, 0.87)",
  textColorSecondary: "rgba(0, 0, 0, 0.6)"
};
var forestGreenTheme = {
  id: "forest",
  name: "Forest Green",
  colors: {
    primary: "#2E7D32",
    primaryLight: "#4CAF50",
    primaryDark: "#1B5E20",
    secondary: "#795548",
    secondaryLight: "#A1887F",
    secondaryDark: "#5D4037",
    background: "#F5F8F5"
  },
  gradient: "linear-gradient(135deg, #2E7D32 0%, #4CAF50 50%, #81C784 100%)",
  glowColor: "rgba(46, 125, 50, 0.6)",
  textColor: "rgba(0, 0, 0, 0.87)",
  textColorSecondary: "rgba(0, 0, 0, 0.6)"
};
var colorThemes = [
  paneonNaturalTheme,
  oceanBlueTheme,
  sunsetOrangeTheme,
  lavenderPurpleTheme,
  forestGreenTheme
];
function getThemeById(id) {
  return colorThemes.find((theme) => theme.id === id);
}
function getDefaultTheme() {
  return paneonNaturalTheme;
}
var THEME_MODE_KEY = "paneon-theme-mode";
function getSystemThemeMode() {
  if (!isBrowser) return "light";
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDark ? "dark" : "light";
}
function getStoredThemeMode() {
  const stored = localStorageUtils.get(THEME_MODE_KEY);
  return stored || "system";
}
function saveThemeMode(mode) {
  localStorageUtils.set(THEME_MODE_KEY, mode);
}
function getEffectiveThemeMode(mode) {
  if (mode === "system") {
    return getSystemThemeMode() === "dark" ? "dark" : "light";
  }
  return mode;
}
function onSystemThemeChange(callback) {
  if (!isBrowser) return () => {
  };
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = (e) => {
    callback(e.matches);
  };
  mediaQuery.addEventListener("change", handler);
  return () => {
    mediaQuery.removeEventListener("change", handler);
  };
}
var THEME_ID_KEY = "paneon-theme-id";
var ThemeContext = createContext(null);
function createMuiTheme(colorTheme, isDarkMode) {
  return createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: colorTheme.colors.primary,
        light: colorTheme.colors.primaryLight,
        dark: colorTheme.colors.primaryDark
      },
      secondary: {
        main: colorTheme.colors.secondary,
        light: colorTheme.colors.secondaryLight,
        dark: colorTheme.colors.secondaryDark
      },
      background: {
        default: isDarkMode ? "#121212" : colorTheme.colors.background,
        paper: isDarkMode ? "#1E1E1E" : "#FFFFFF"
      },
      text: {
        primary: isDarkMode ? "rgba(255, 255, 255, 0.87)" : colorTheme.textColor,
        secondary: isDarkMode ? "rgba(255, 255, 255, 0.6)" : colorTheme.textColorSecondary
      }
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 }
    },
    shape: {
      borderRadius: 12
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            fontWeight: 600
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 16
          }
        }
      }
    }
  });
}
function PaneonThemeProvider({
  children,
  defaultThemeId = "paneon",
  themes = colorThemes
}) {
  const [themeId, setThemeId] = useState(() => {
    const stored = localStorageUtils.get(THEME_ID_KEY);
    return stored || defaultThemeId;
  });
  const [themeMode, setThemeModeState] = useState(getStoredThemeMode);
  const [isDarkMode, setIsDarkMode] = useState(() => getEffectiveThemeMode(themeMode) === "dark");
  const currentTheme = useMemo(() => {
    return getThemeById(themeId) || getDefaultTheme();
  }, [themeId]);
  const muiTheme = useMemo(() => {
    return createMuiTheme(currentTheme, isDarkMode);
  }, [currentTheme, isDarkMode]);
  useEffect(() => {
    setIsDarkMode(getEffectiveThemeMode(themeMode) === "dark");
  }, [themeMode]);
  useEffect(() => {
    if (themeMode !== "system") return;
    return onSystemThemeChange((isDark) => {
      setIsDarkMode(isDark);
    });
  }, [themeMode]);
  const setTheme = useCallback((id) => {
    setThemeId(id);
    localStorageUtils.set(THEME_ID_KEY, id);
  }, []);
  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    saveThemeMode(mode);
  }, []);
  const contextValue = {
    currentTheme,
    themeMode,
    availableThemes: themes,
    setTheme,
    setThemeMode,
    isDarkMode
  };
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsxs(ThemeProvider, { theme: muiTheme, children: [
    /* @__PURE__ */ jsx(CssBaseline, {}),
    children
  ] }) });
}
function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a PaneonThemeProvider");
  }
  return context;
}

export { PaneonThemeProvider, colorThemes, forestGreenTheme, getDefaultTheme, getEffectiveThemeMode, getStoredThemeMode, getSystemThemeMode, getThemeById, lavenderPurpleTheme, oceanBlueTheme, onSystemThemeChange, paneonNaturalTheme, saveThemeMode, sunsetOrangeTheme, useTheme };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map